import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { BillingAdmin } from 'frontend-payments/models';
import type { Model } from 'frontend-payments/routes/authenticated/billing-admins';

export default class AuthenticatedBillingAdminsController extends Controller {
  declare model: Model;

  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare session: Services['session'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked editingBillingAdmin: BillingAdmin | null = null;
  @tracked showBillingAdminsModal = false;

  createBillingAdminTask = task(
    async (userData: {
      email: string;
      firstName: string;
      lastName: string;
    }): Promise<void> => {
      try {
        // Create new billing admin
        const billingAdmin = this.store.createRecord<BillingAdmin>(
          'billing-admin',
          {
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
          },
        );

        this.closeBillingAdminsModal();
        await this.addBillingAdminRecord(billingAdmin);
      } catch (error) {
        // Handle any unexpected errors during record creation
        this.errorTracking.captureException(error);
        this.toast.error(this.intl.t('general.error'), {
          showIcon: true,
        });
      }
    },
  );

  editBillingAdminTask = task(
    async (userData: {
      email: string;
      firstName: string;
      lastName: string;
    }): Promise<void> => {
      if (!this.editingBillingAdmin) {
        return;
      }

      try {
        // Edit existing billing admin
        const billingAdmin = this.editingBillingAdmin;
        billingAdmin.email = userData.email;
        billingAdmin.firstName = userData.firstName;
        billingAdmin.lastName = userData.lastName;

        this.closeBillingAdminsModal();
        await this.editBillingAdminRecord(billingAdmin);
      } catch (error) {
        // Handle any unexpected errors during record editing
        this.errorTracking.captureException(error);
        this.toast.error(this.intl.t('general.error'), {
          showIcon: true,
        });
      }
    },
  );

  removeBillingAdminTask = task(
    async (billingAdmin: BillingAdmin): Promise<void> => {
      try {
        // destroyRecord automatically removes from store (optimistic)
        await billingAdmin.destroyRecord({
          adapterOptions: {
            namespace: `api/v4/accounts/${this.accountId}`,
          },
        });

        this.toast.success(
          this.intl.t('payment.billing_admins.billing_administrator_deleted'),
          { showIcon: true },
        );

        this.usageAnalytics.track('Billing Admin Removed', {
          eventTrigger: 'alert',
        });
      } catch (error) {
        this.errorTracking.captureException(error);
        this.toast.error(this.intl.t('general.error'), {
          showIcon: true,
        });
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get accountId() {
    return this.model.accountId;
  }

  get billingAdmins(): BillingAdmin[] {
    // Directly use peekAll to get live-updating list from ember-data store
    // This automatically includes new records created with createRecord
    // and automatically removes records deleted with destroyRecord
    return [...this.store.peekAll<BillingAdmin>('billing-admin')];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isAddUserButtonDisabled() {
    return this.isBillingAdminsLoading || this.session.isStaffUser;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBillingAdminsLoading() {
    return (
      this.model.billingAdmins.isRunning || this.model.parentAccount?.isRunning
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isEditMode() {
    return this.editingBillingAdmin !== null;
  }

  async addBillingAdminRecord(billingAdmin: BillingAdmin): Promise<void> {
    try {
      this.usageAnalytics.track('Billing Admin Added', {
        eventTrigger: 'header',
      });

      await billingAdmin.save({
        adapterOptions: {
          namespace: `api/v4/accounts/${this.accountId}`,
        },
      });

      this.toast.success(
        this.intl.t('payment.billing_admins.billing_administrator_created'),
        { showIcon: true },
      );
    } catch (error) {
      billingAdmin.errorMessage = this.intl.t(
        'payment.billing_admins.action_failed',
      );

      // Remove the failed record after 5 seconds
      setTimeout(() => {
        // Unload the record from the store, which removes it from peekAll
        if (billingAdmin && !billingAdmin.isDestroyed) {
          billingAdmin.unloadRecord();
        }
      }, 5000);

      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error'), { showIcon: true });
    }
  }

  async editBillingAdminRecord(billingAdmin: BillingAdmin): Promise<void> {
    try {
      this.usageAnalytics.track('Billing Admin Updated', {
        eventTrigger: 'modal',
      });

      await billingAdmin.save({
        adapterOptions: {
          namespace: `api/v4/accounts/${this.accountId}`,
        },
      });

      this.toast.success(
        this.intl.t('payment.billing_admins.billing_administrator_updated'),
        { showIcon: true },
      );
    } catch (error) {
      if (billingAdmin) {
        billingAdmin.rollbackAttributes();
      }

      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error'), { showIcon: true });
    }
  }

  @action
  closeBillingAdminsModal(): void {
    this.showBillingAdminsModal = false;
    this.editingBillingAdmin = null;
  }

  @action
  onEditUser(billingAdmin: BillingAdmin): void {
    this.editingBillingAdmin = billingAdmin;
    this.openBillingAdminsModal();
  }

  @action
  async onRemoveUser(billingAdmin: BillingAdmin): Promise<void> {
    const shouldDelete = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('payments.billing_admins.remove_user_title'),
      text: this.intl.t('payments.billing_admins.remove_user_message'),
      buttons: {
        cancel: { text: this.intl.t('general.cancel') },
        confirm: {
          text: this.intl.t('payments.billing_admins.remove_user_title'),
        },
      },
    });

    if (!shouldDelete) {
      return;
    }

    await this.removeBillingAdminTask.perform(billingAdmin);
  }

  @action
  openBillingAdminsModal(): void {
    if (this.isBillingAdminsLoading) {
      return;
    }

    this.showBillingAdminsModal = true;
  }
}
